import './AudienceChoiceGroup.scss';
import { FileShareType, SharingAudience, SharingLinkKind, ClientId } from '../../../interfaces/SharingInterfaces';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { ShareLinkDescription } from '../../ShareLinkDescription/ShareLinkDescription';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import stringsResx from '../../Share.resx';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import * as ClientIdHelper from '../../../utilities/ClientIdHelper';

export interface IAudienceChoiceGroupProps {
    items: Array<IAudienceChoice>;
    onAudienceChange?: (audience: SharingAudience) => void;
    onChange?: (key: SharingAudience) => void;
    companyName?: string;
    clientId: ClientId;
}

export interface IAudienceChoiceGroupState {
    selectedKey: SharingAudience;
}

export interface IAudienceChoice {
    icon: string;
    iconUrl?: string;
    isChecked: boolean;
    isDisabled: boolean;
    key: SharingAudience;
    label: string;
    linkKinds: Array<SharingLinkKind>;
    permissionsType: FileShareType;
    disabledText?: string;
}

export class AudienceChoiceGroup extends React.Component<IAudienceChoiceGroupProps, IAudienceChoiceGroupState> {
    private _platformDetection: PlatformDetection;

    static contextTypes = {
        platformDetection: PropTypes.object.isRequired,
    };

    constructor(props: IAudienceChoiceGroupProps, context: any) {
        super(props);

        this.state = {
            selectedKey: this._getSelectedIndex(props)
        };

        this._platformDetection = context.platformDetection;
    }

    private _getSelectedIndex(props: IAudienceChoiceGroupProps): SharingAudience {
        for (const item of props.items) {
            if (item.isChecked) {
                return item.key;
            }
        }
    }

    public render(): React.ReactElement<{}> {
        const rows = this.props.items.map(this._renderItem, this);

        return (
            <div
                aria-label={ stringsResx.permissionsSettingsHeader }
                role='radiogroup'
            >
                <FocusZone
                    direction={ FocusZoneDirection.vertical }
                    defaultActiveElement={ '.od-AudienceChoiceGroup-row.is-selected' }
                    onActiveElementChanged={ this._onActiveElementChanged }>
                    { rows }
                </FocusZone>
            </div>
        );
    }

    private _renderItem(item: IAudienceChoice) {
        const key = item.key;

        // Temporarily disable infobutton message in FF since divs inside buttons are not getting click/hover events
        let infoButtonSupported = !this._platformDetection.isFirefox;
        let infoButtonMessage = item.isDisabled && item.disabledText ? item.disabledText : '';
        const state = this.state;
        const isSelected = state.selectedKey === key;

        return (
            <div key={ key }>
                <button
                    role='radio'
                    aria-checked={ isSelected }
                    key={ key }
                    onClick={ item.isDisabled ? () => { return; } : this._onRowClick.bind(this, key) }
                    className={ this._getRowClasses(item) }
                    autoFocus={ isSelected }
                    aria-disabled={ item.isDisabled }
                    disabled={ item.isDisabled }
                    aria-label={ `${item.label} ${infoButtonMessage}` }
                    data-key={ key }
                >
                    <span
                        tabIndex={ -1 }
                        role='status'
                        aria-live='polite'
                        aria-label={ isSelected ? StringHelper.format(stringsResx.audienceSelectedStatus, item.label) : '' }
                        className='od-Share-screenReaderOnly'
                    >
                        { isSelected ? StringHelper.format(stringsResx.audienceSelectedStatus, item.label) : '' }
                    </span>
                    <div className='od-AudienceChoiceGroup-description'>
                        <ShareLinkDescription
                            label={ item.label }
                            permissionsType={ item.permissionsType }
                            showLabel={ true }
                            iconUrl={ item.iconUrl }
                            infoButtonMessage={ infoButtonSupported ? infoButtonMessage : '' }
                        />
                    </div>
                    <div className='tickIcon' aria-hidden={ true }></div>
                </button>
                <div className='od-AudienceChoiceGroup-bottomBorder' />
            </div>
        );
    }

    @autobind
    private _onActiveElementChanged(audienceOptionWithFocus: HTMLElement) {
        if (this._platformDetection.isMobile || ClientIdHelper.isMobile(this.props.clientId) || !audienceOptionWithFocus || !audienceOptionWithFocus.hasAttribute('data-key')) {
            return;
        }
        const sharingAudience = parseInt(audienceOptionWithFocus.getAttribute('data-key'), 10) as SharingAudience;
        const isDisabled = audienceOptionWithFocus.getAttribute('aria-disabled') === 'true';
        if (!isDisabled) {
            this._onRowClick(sharingAudience, null);
        }
    }

    private _getRowClasses(item: IAudienceChoice) {
        let classes = 'od-ModifyPermissions-margins od-AudienceChoiceGroup-row';

        if (this.state.selectedKey === item.key) {
            classes += ' is-selected';
        }

        if (item.isDisabled) {
            classes += ' is-disabled';
        }

        return classes;
    }

    private _onRowClick(key: SharingAudience, evt: React.SyntheticEvent<{}>): void {
        const props = this.props;

        // Sets the selected item as selected in the UI.
        this.setState({ selectedKey: key });
        props.onChange(key);

        // Changes the audience of selectedPermissions.
        props.onAudienceChange(key);
    }
}
